import React, { useEffect, useState, useId } from "react"
import { useSmartScript } from "./SmartScriptProvider"
import Box from "@/components/Box"
import Logo from "@/images/cdc-logo.webp"

const qrOptions = {
  logo: Logo,
  codeColor: "#002F61",
}

type OneLinkQrCodeProps = {
  onRenderResult?: (result: "success" | "failure") => void
  isDarkContainer?: boolean
  size?: string
}

export const OneLinkQrCode: React.FC<OneLinkQrCodeProps> = ({
  onRenderResult = () => {
    return
  },
  isDarkContainer = false,
  size = "calc(100% - 15px)",
}) => {
  const { oneLinkUrl } = useSmartScript()
  const [renderFailed, setRenderFailed] = useState(false)
  // Use a unique ID for each QR code instance to avoid conflicts
  const uniqueId = useId()
  const qrId = `af-onelink-qr-${uniqueId}`

  useEffect(() => {
    // Don't run on server or if URL isn't ready
    if (typeof window === "undefined" || !oneLinkUrl) {
      if (!oneLinkUrl) {
        // If no URL, fail immediately
        onRenderResult("failure")
        setRenderFailed(true)
      }
      return
    }

    let attempts = 0
    const maxAttempts = 10 // Poll for 3 seconds (10 * 300ms)
    let intervalId: number | null = null

    const tryRender = () => {
      const smartScript = (window as any).AF_SMART_SCRIPT
      const container = document.getElementById(qrId)

      //Check if script and container are ready
      if (
        smartScript &&
        typeof smartScript.displayQrCode === "function" &&
        container
      ) {
        if (intervalId) clearInterval(intervalId) // Stop polling

        try {
          // Prevent re-drawing if already rendered
          if (container.querySelector("canvas")) {
            onRenderResult("success")
            return
          }
          smartScript.displayQrCode(qrId, qrOptions)
          onRenderResult("success")
        } catch (e) {
          onRenderResult("failure")
          setRenderFailed(true)
        }
      } else {
        attempts++
        if (attempts >= maxAttempts) {
          if (intervalId) clearInterval(intervalId)
          onRenderResult("failure")
          setRenderFailed(true)
        }
      }
    }

    // Start polling
    intervalId = window.setInterval(tryRender, 300)

    // Cleanup function to stop polling if the component unmounts
    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [oneLinkUrl, onRenderResult, qrId])

  if (renderFailed) {
    return (
      <Box
        as="img"
        src={
          isDarkContainer
            ? `${process.env.ASSETS_BASE_URL}/app_qr_code_logo.webp`
            : `${process.env.ASSETS_BASE_URL}/app-qr-code-logo-blue.webp`
        }
        width="100%"
        height="100%"
        borderRadius="inherit"
        m={"auto"}
        backgroundColor={isDarkContainer ? "transparent" : "white"}
      />
    )
  }

  // This container is the target for the smart script
  return (
    <Box
      id={qrId}
      customCss={{
        margin: "auto",
        width: size,
        height: size,
        "& > canvas": {
          width: "100% !important",
          height: "100% !important",
        },
      }}
    />
  )
}
